import { useEffect, useState } from "react";
import { Loader2, LocateFixed } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Field } from "@/components/common";
import { api, brl, errMsg } from "@/lib/api";
import { PinMap } from "./mapParts";

export const fullAddress = (a = {}) => [
  [a.street, a.number].filter(Boolean).join(", ") + (a.complement ? ` - ${a.complement}` : ""),
  a.district, [a.city, a.state].filter(Boolean).join("/"), a.cep && `CEP ${a.cep}`,
].filter(Boolean).join(" - ");

function QuoteBox({ q }) {
  if (q.ok) return (
    <div className="rounded-lg bg-emerald-50 p-3 text-sm text-emerald-700" data-testid="delivery-quote-ok">
      <p className="font-semibold">Entregamos aqui · {q.area}</p>
      <p>Taxa {brl(q.fee)}{q.eta ? <> · chega em <b data-testid="delivery-quote-eta">~{q.eta} min</b></> : ""}{q.distance_km != null ? ` · ${q.distance_km.toFixed(1)} km` : ""}</p>
    </div>
  );
  return <p className="rounded-lg bg-red-50 p-3 text-sm font-medium text-red-600" data-testid="delivery-quote-blocked">{q.message}</p>;
}

const gpsPoint = () => new Promise((ok) => {
  if (!navigator.geolocation) return ok(null);
  navigator.geolocation.getCurrentPosition((p) => ok([p.coords.latitude, p.coords.longitude]), () => ok(null), { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 });
});

function GpsConfirm({ found, onYes, onNo }) {
  return (
    <div className="space-y-3 rounded-xl border-2 border-blue-500 bg-blue-50 p-3 text-slate-800" data-testid="gps-confirm">
      <p className="font-semibold">Você está nesta localização?</p>
      <p className="text-sm" data-testid="gps-confirm-address">{found ? fullAddress(found) : "Ponto do GPS marcado no mapa"}</p>
      <p className="text-xs text-slate-500">Confira no mapa. Se precisar, arraste o mapa até o ponto vermelho ficar na sua porta.</p>
      <div className="grid grid-cols-2 gap-2">
        <Button type="button" className="h-11" onClick={onYes} data-testid="gps-confirm-yes">Sim, é aqui</Button>
        <Button type="button" variant="outline" className="h-11 bg-white text-slate-800" onClick={onNo} data-testid="gps-confirm-no">Não, usar endereço</Button>
      </div>
    </div>
  );
}

export function CustomerAddress({ slug, st, value, onChange, inputCls = "", lazyMap = false }) {
  const { addr = {}, point, quote } = value;
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");
  const [manual, setManual] = useState(false);
  const [focus, setFocus] = useState(null);
  const [gps, setGps] = useState(null);
  const [shown, setShown] = useState(!lazyMap || !!quote);
  const adjust = st.delivery_pin_adjust !== false;
  const store = st.store_location?.lat != null ? [st.store_location.lat, st.store_location.lng] : null;
  const quoteAt = async (p, a = addr) => {
    try {
      const { data } = await api.post(`/public/menu/${slug}/delivery-quote`, { lat: p[0], lng: p[1] });
      onChange({ addr: a, point: p, quote: data });
    } catch (e) { setNote(errMsg(e)); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (!lazyMap && point && !quote) quoteAt(point); }, []);
  const edit = (patch) => {
    const keep = Object.keys(patch).every((k) => k === "number" || k === "complement");
    onChange(keep ? { ...value, addr: { ...addr, ...patch } } : { addr: { ...addr, ...patch }, point: null, quote: null });
    setNote("");
  };
  const onCep = async (v) => {
    edit({ cep: v });
    const d = v.replace(/\D/g, "");
    if (d.length !== 8) return;
    try {
      const r = await fetch(`https://viacep.com.br/ws/${d}/json/`).then((x) => x.json());
      if (!r.erro) edit({ cep: v, street: r.logradouro || addr.street || "", district: r.bairro || addr.district || "", city: r.localidade || addr.city || "", state: r.uf || addr.state || "" });
    } catch { /* CEP opcional */ }
  };
  const byAddress = async () => {
    setShown(true); setBusy(true); setNote(""); setManual(false); setGps(null);
    if (!addr.street || !addr.city) {
      setNote(adjust ? "Digite o endereço e toque de novo, ou marque o ponto direto no mapa abaixo." : "Digite rua e cidade e toque de novo.");
      setManual(adjust); setBusy(false);
      return;
    }
    try {
      const { data } = await api.post(`/public/menu/${slug}/geocode`, addr);
      const p = [data.lat, data.lng];
      setFocus(p);
      await quoteAt(p);
      if (data.precision !== "endereco" && adjust) setNote("Localização aproximada. Confira e, se precisar, arraste o mapa até o ponto vermelho ficar na sua porta.");
    } catch (e) {
      setNote(adjust ? `${errMsg(e)} Você pode marcar o ponto direto no mapa abaixo.` : errMsg(e));
      setManual(adjust);
    } finally { setBusy(false); }
  };
  const locate = async () => {
    setShown(true); setBusy(true); setNote(""); setGps(null);
    const p = await gpsPoint();
    if (!p) return byAddress();
    let found = null;
    try { ({ data: found } = await api.post(`/public/menu/${slug}/reverse-geocode`, { lat: p[0], lng: p[1] })); } catch { /* endereço do ponto é opcional */ }
    setFocus(p); setGps({ point: p, found }); setBusy(false);
  };
  const confirmGps = async () => {
    const f = gps.found || {};
    const a = f.city ? { ...addr, street: f.street || addr.street, district: f.district, city: f.city, state: f.state || addr.state, cep: f.cep || addr.cep, number: addr.number || f.number } : addr;
    const p = gps.point;
    setGps(null);
    if (!a.number) setNote("Confira o endereço e digite o número da casa.");
    await quoteAt(p, a);
  };
  const inp = (k, label, cls = "", extra = {}) => (
    <Field label={label} className={cls}><Input value={addr[k] || ""} onChange={(e) => (k === "cep" ? onCep(e.target.value) : edit({ [k]: e.target.value }))} className={inputCls} data-testid={`addr-${k}`} {...extra} /></Field>
  );
  return (
    <div className="space-y-3" data-testid="customer-address">
      <div className="grid grid-cols-3 gap-2">
        {inp("cep", "CEP", "", { inputMode: "numeric", maxLength: 9 })}
        {inp("street", "Rua", "col-span-2")}
        {inp("number", "Número")}
        {inp("district", "Bairro", "col-span-2")}
        {inp("complement", "Complemento / referência", "col-span-3")}
        {inp("city", "Cidade", "col-span-2")}
        {inp("state", "UF", "", { maxLength: 2 })}
      </div>
      <Button type="button" className="h-14 w-full text-base font-semibold shadow-md" onClick={locate} disabled={busy} data-testid="locate-address-btn">
        {busy ? <Loader2 className="mr-2 h-5 w-5 animate-spin" /> : <LocateFixed className="mr-2 h-5 w-5" />}Localizar e calcular a entrega
      </Button>
      <p className="-mt-1 text-[11px] text-slate-500">Pedimos a localização do celular para achar seu endereço (opcional). Sem GPS, usamos o endereço digitado.</p>
      {note && <p className="text-xs text-amber-700" data-testid="address-note">{note}</p>}
      {gps && <GpsConfirm found={gps.found} onYes={confirmGps} onNo={byAddress} />}
      {(gps || (shown && adjust && (point || manual))) && <PinMap point={gps ? gps.point : point} start={store} focus={focus} onPick={(p) => (gps ? setGps((g) => ({ ...g, point: p })) : quoteAt(p))} />}
      {!gps && shown && adjust && (point || manual) && <p className="text-[11px] text-slate-500" data-testid="pin-map-hint">Arraste o mapa até o ponto vermelho ficar na sua porta.</p>}
      {shown && quote && <QuoteBox q={quote} />}
    </div>
  );
}
